import React, { useState, useEffect } from 'react';
import { X, QrCode, CheckCircle2, Copy, Check, ShieldCheck, Globe, ExternalLink, Sparkles, Paperclip, UploadCloud, Trash2, AlertCircle, DollarSign, Zap } from 'lucide-react';
import { PaymentGateway, PaymentDetails, CustomCharge } from '../types';
import { generatePixData, getEffectiveCurrencyRates } from '../lib/shippingAndPayment';
import { convertToWebPDataUrl } from '../lib/imageUtils';
import { getSavedPaymentGatewayConfigs } from './PaymentMethodsModal';
import PayPalSmartButtons from './PayPalSmartButtons';
import MercadoPagoSmartButtons from './MercadoPagoSmartButtons';

interface CustomChargePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  charge: CustomCharge;
  onPaymentComplete: (chargeId: string, paymentDetails: PaymentDetails) => Promise<void> | void;
}

export const CustomChargePaymentModal: React.FC<CustomChargePaymentModalProps> = ({
  isOpen,
  onClose,
  charge,
  onPaymentComplete
}) => {
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway>('Wise');
  const [copiedPix, setCopiedPix] = useState(false);
  const [copiedWise, setCopiedWise] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [savedGatewayConfigs, setSavedGatewayConfigs] = useState<any>({});
  const [receiptFile, setReceiptFile] = useState<{ url: string; name: string; type: string } | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedGateway('Wise');
      setSavedGatewayConfigs(getSavedPaymentGatewayConfigs());
      setReceiptFile(null);
      setReceiptError(null);
    }
  }, [isOpen]);

  const rates = getEffectiveCurrencyRates();
  const baseAmountBrl = charge.amountJpy * rates.jpyToBrl;
  const internationalFeeBrl = baseAmountBrl * (rates.internationalFeePercent || 0.07);
  const amountBrl = baseAmountBrl + internationalFeeBrl;
  const totalAmountJpy = Math.round(charge.amountJpy * (1 + (rates.internationalFeePercent || 0.07)));

  // Pix Logic
  const defaultPixData = generatePixData(charge.id, amountBrl, "Egami Corp");
  const pixData = {
    ...defaultPixData,
    pixCopiaECola: savedGatewayConfigs.PIX?.pixCopiaECola || defaultPixData.pixCopiaECola
  };

  const handleCopyPix = () => {
    if (pixData.pixCopiaECola) {
      navigator.clipboard.writeText(pixData.pixCopiaECola);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2000);
    }
  };

  const handleCopyWise = () => {
    const email = savedGatewayConfigs.Wise?.wiseEmail || 'egami.corporation@gmail.com';
    navigator.clipboard.writeText(email);
    setCopiedWise(true);
    setTimeout(() => setCopiedWise(false), 2000);
  };

  const handleReceiptChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setReceiptError(null);
    if (file.type.startsWith('image/')) {
      try {
        const webpDataUrl = await convertToWebPDataUrl(file, { maxWidth: 1920, quality: 0.85 });
        const newName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        setReceiptFile({
          url: webpDataUrl,
          name: `${newName}.webp`,
          type: 'image/webp'
        });
      } catch (err) {
        console.error('Erro ao converter comprovante:', err);
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptFile({
            url: reader.result as string,
            name: file.name,
            type: file.type
          });
        };
        reader.readAsDataURL(file);
      }
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptFile({
          url: reader.result as string,
          name: file.name,
          type: file.type
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirmPayment = async () => {
    if (selectedGateway === 'PIX' && !receiptFile) {
      setReceiptError('⚠️ O comprovante de pagamento PIX é obrigatório. Por favor, anexe a foto ou PDF do comprovante.');
      return;
    }

    setLoading(true);
    setReceiptError(null);
    try {
      await onPaymentComplete(charge.id, {
        method: selectedGateway,
        status: 'Aprovado',
        amountUsd: totalAmountJpy * (rates.jpyToUsd || 0.0065),
        amountJpy: totalAmountJpy,
        baseAmountBrl: baseAmountBrl,
        internationalFeeBrl: internationalFeeBrl,
        totalAmountBrl: amountBrl,
        amountTotalBrl: amountBrl,
        transactionId: 'simulated-' + Date.now(),
        paidAt: new Date().toISOString(),
        proofReceiptUrl: receiptFile?.url,
        proofReceiptName: receiptFile?.name
      });
      setPaid(true);
      setTimeout(() => {
        onClose();
        setPaid(false);
      }, 2000);
    } catch (error) {
      console.error(error);
      alert('Erro ao confirmar pagamento');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (paid) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-emerald-600 animate-in fade-in duration-300">
        <div className="text-center text-white p-8">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-short shadow-2xl">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black mb-2 tracking-tight">Pagamento Confirmado!</h2>
          <p className="text-emerald-100 font-medium">Ordem de pagamento quitada com sucesso.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full sm:max-w-xl min-h-screen sm:min-h-0 sm:rounded-3xl shadow-2xl flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 pt-10 sm:pt-6 pb-8 relative shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors cursor-pointer">
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex flex-col items-center text-center mt-2">
            <span className="px-3 py-1 bg-amber-500 text-amber-950 text-[10px] font-black uppercase tracking-widest rounded-full mb-3 shadow-sm border border-amber-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-900" />
              Pagamento Avulso
            </span>
            <h2 className="text-2xl font-black text-white mb-2">{charge.title}</h2>
            {charge.description && (
              <p className="text-xs text-slate-300 max-w-md mb-2">{charge.description}</p>
            )}
            <div className="text-5xl font-black text-white tracking-tighter tabular-nums drop-shadow-md">
              <span className="text-2xl text-emerald-400 font-bold mr-1 align-top">R$</span>
              {amountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs font-medium text-slate-300 mt-2">
              Valor Base: R$ {baseAmountBrl.toFixed(2)} (¥ {charge.amountJpy.toLocaleString('ja-JP')}) + Taxa Internacional (7%): R$ {internationalFeeBrl.toFixed(2)}
            </div>
            <div className="text-xs font-bold text-emerald-400 mt-1">
              Total Equivalente c/ Taxas: ¥ {totalAmountJpy.toLocaleString('ja-JP')} JPY
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50 flex flex-col">
          {/* Seletor de Métodos: Wise (padrão), PIX, PayPal e Mercado Pago */}
          <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <button
              onClick={() => setSelectedGateway('Wise')}
              className={`p-3 border rounded-2xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer relative ${
                selectedGateway === 'Wise'
                  ? 'bg-[#163300] border-[#163300] text-[#9FE870] shadow-md ring-2 ring-[#9FE870]'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <Globe className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedGateway === 'Wise' ? 'text-[#9FE870]' : 'text-[#163300]'}`} />
                <span className="font-extrabold text-xs sm:text-sm">Wise</span>
              </div>
              <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${selectedGateway === 'Wise' ? 'bg-[#9FE870]/25 text-[#9FE870]' : 'bg-emerald-100 text-emerald-800'}`}>
                Recomendado
              </span>
            </button>

            <button
              onClick={() => setSelectedGateway('PIX')}
              className={`p-3 border rounded-2xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                selectedGateway === 'PIX'
                  ? 'bg-emerald-600 border-emerald-600 text-white shadow-md ring-2 ring-emerald-400'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <QrCode className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedGateway === 'PIX' ? 'text-white' : 'text-emerald-600'}`} />
                <span className="font-extrabold text-xs sm:text-sm">PIX</span>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${selectedGateway === 'PIX' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                Instantâneo
              </span>
            </button>

            <button
              onClick={() => setSelectedGateway('PayPal')}
              className={`p-3 border rounded-2xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                selectedGateway === 'PayPal'
                  ? 'bg-blue-600 border-blue-600 text-white shadow-md ring-2 ring-blue-400'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <DollarSign className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedGateway === 'PayPal' ? 'text-white' : 'text-blue-600'}`} />
                <span className="font-extrabold text-xs sm:text-sm">PayPal</span>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${selectedGateway === 'PayPal' ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-700'}`}>
                Oficial & Cards
              </span>
            </button>

            <button
              onClick={() => setSelectedGateway('MercadoPago')}
              className={`p-3 border rounded-2xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                selectedGateway === 'MercadoPago'
                  ? 'bg-[#009EE3] border-[#009EE3] text-white shadow-md ring-2 ring-sky-300'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <Zap className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedGateway === 'MercadoPago' ? 'text-white' : 'text-[#009EE3]'}`} />
                <span className="font-extrabold text-xs sm:text-sm">Mercado Pago</span>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${selectedGateway === 'MercadoPago' ? 'bg-white/20 text-white' : 'bg-sky-50 text-sky-700'}`}>
                SDK Oficial
              </span>
            </button>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm grow flex flex-col">
            {selectedGateway === 'Wise' && (
              <div className="space-y-4">
                <div className="p-3 bg-[#163300] text-[#9FE870] rounded-xl flex items-center justify-between gap-2">
                  <span className="text-xs font-bold">Não tem conta na Wise?</span>
                  <a
                    href={savedGatewayConfigs.Wise?.wiseInviteUrl || 'https://wise.com/invite/ilpc/welingtone'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 bg-[#9FE870] hover:bg-[#8edb5f] text-[#163300] font-black text-xs rounded-lg flex items-center gap-1 shrink-0 transition-colors"
                  >
                    <span>Crie sua conta na Wise</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <span className="text-xs text-slate-500 font-bold uppercase block">Conta / E-mail de Destino Wise:</span>
                  <div className="flex items-center justify-between gap-2 bg-white p-2.5 rounded-lg border border-slate-200">
                    <span className="text-sm font-mono font-bold text-slate-900 truncate">
                      {savedGatewayConfigs.Wise?.wiseEmail || 'egami.corporation@gmail.com'}
                    </span>
                    <button onClick={handleCopyWise} className="px-3 py-1.5 bg-[#163300] hover:bg-[#204900] text-[#9FE870] rounded-lg font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors shrink-0">
                      {copiedWise ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedWise ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                  <div className="flex flex-col gap-1 text-xs text-slate-600">
                    <p>
                      Titular: <strong>{savedGatewayConfigs.Wise?.wiseAccountName || 'Egami Corporation'}</strong>
                    </p>
                    <p className="text-slate-500 text-[11px]">
                      Você pode transferir tanto em <strong>JPY (Ienes)</strong> quanto em <strong>BRL (Reais)</strong> pelo app ou site da Wise.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedGateway === 'PIX' && (
              <div className="text-center space-y-4">
                <div className="p-3 bg-slate-50 rounded-xl inline-block border border-slate-200">
                  <img
                    src={savedGatewayConfigs.PIX?.pixQrCodeImage || `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixData.pixCopiaECola)}`}
                    alt="QR Code PIX"
                    className="w-48 h-48 mx-auto mix-blend-multiply"
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 mb-2">Pix Copia e Cola</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={pixData.pixCopiaECola}
                      className="w-full text-xs font-mono p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 outline-none"
                    />
                    <button onClick={handleCopyPix} className="p-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-colors cursor-pointer shrink-0">
                      {copiedPix ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Comprovante PIX Obrigatório */}
                <div className="p-4 bg-emerald-50/70 rounded-2xl border-2 border-emerald-300/80 shadow-xs space-y-3 text-left mt-4">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black text-emerald-950 flex items-center gap-1.5">
                      <Paperclip className="w-4 h-4 text-emerald-700" />
                      <span>Comprovante de Pagamento PIX (Obrigatório)</span>
                    </label>
                    {receiptFile ? (
                      <span className="px-2 py-0.5 bg-emerald-600 text-white font-black text-[10px] rounded-full flex items-center gap-1 shadow-2xs">
                        <CheckCircle2 className="w-3 h-3" />
                        Foto Anexada
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-amber-500 text-slate-950 font-black text-[10px] rounded-full">
                        * Obrigatório
                      </span>
                    )}
                  </div>

                  <p className="text-[11px] text-slate-600">
                    O envio do comprovante é obrigatório para validação da equipe de finanças da Egami Corp.
                  </p>

                  {receiptFile ? (
                    <div className="p-3 bg-white rounded-xl border border-emerald-300 flex items-center justify-between gap-3 shadow-2xs">
                      <div className="flex items-center gap-3 overflow-hidden">
                        {receiptFile.type.startsWith('image/') ? (
                          <img src={receiptFile.url} alt="Comprovante" className="w-12 h-12 object-cover rounded-lg border border-emerald-300 flex-shrink-0 shadow-2xs" />
                        ) : (
                          <div className="w-12 h-12 bg-emerald-100 text-emerald-800 rounded-lg flex items-center justify-center font-black text-xs flex-shrink-0 border border-emerald-300">
                            PDF
                          </div>
                        )}
                        <div className="overflow-hidden">
                          <span className="text-xs font-bold text-slate-800 truncate block">{receiptFile.name}</span>
                          <span className="text-[10px] text-emerald-700 font-bold block">✓ Comprovante pronto para validação</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setReceiptFile(null)}
                        className="p-2 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors flex-shrink-0 cursor-pointer"
                        title="Remover comprovante"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="border-2 border-dashed border-emerald-400 hover:border-emerald-600 bg-white hover:bg-emerald-50/50 transition-all rounded-xl p-4 flex flex-col items-center justify-center gap-1.5 cursor-pointer group text-center shadow-2xs">
                      <input
                        type="file"
                        accept="image/png, image/jpeg, image/webp, application/pdf"
                        className="hidden"
                        onChange={handleReceiptChange}
                      />
                      <UploadCloud className="w-7 h-7 text-emerald-600 group-hover:scale-110 transition-transform" />
                      <span className="text-xs font-black text-emerald-950">Clique para selecionar a Foto ou PDF do Comprovante</span>
                      <span className="text-[10px] text-slate-500 font-medium">Formatos aceitos: JPG, PNG, WEBP ou PDF</span>
                    </label>
                  )}

                  {receiptError && (
                    <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 font-bold flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                      <span>{receiptError}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedGateway === 'PayPal' && (
              <div className="space-y-4">
                <PayPalSmartButtons
                  amountBrl={amountBrl}
                  orderId={`custom_charge_${charge.id}`}
                  customDescription={`Cobrança Avulsa: ${charge.title}`}
                  onSuccess={async (details) => {
                    setLoading(true);
                    try {
                      await onPaymentComplete(charge.id, {
                        method: 'PayPal',
                        status: 'Aprovado',
                        amountUsd: charge.amountJpy * (rates.jpyToUsd || 0.0065),
                        amountJpy: charge.amountJpy,
                        baseAmountBrl: amountBrl,
                        internationalFeeBrl: 0,
                        totalAmountBrl: amountBrl,
                        amountTotalBrl: amountBrl,
                        transactionId: details.transactionId,
                        paidAt: new Date().toISOString(),
                        payerEmail: details.payer?.email_address,
                        rawDetails: details.rawDetails
                      });
                      setPaid(true);
                      setTimeout(() => {
                        onClose();
                        setPaid(false);
                      }, 2000);
                    } catch (err) {
                      console.error('Error completing PayPal charge:', err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onError={(err) => {
                    console.warn('PayPal error in custom charge modal:', err);
                  }}
                />
              </div>
            )}
            {selectedGateway === 'MercadoPago' && (
              <div className="space-y-4">
                <MercadoPagoSmartButtons
                  amountBrl={amountBrl}
                  orderId={`custom_charge_${charge.id}`}
                  customDescription={`Cobrança Avulsa: ${charge.title}`}
                  onSuccess={async (details) => {
                    setLoading(true);
                    try {
                      await onPaymentComplete(charge.id, {
                        method: 'MercadoPago',
                        status: 'Aprovado',
                        amountUsd: charge.amountJpy * (rates.jpyToUsd || 0.0065),
                        amountJpy: charge.amountJpy,
                        baseAmountBrl: amountBrl,
                        internationalFeeBrl: 0,
                        totalAmountBrl: amountBrl,
                        amountTotalBrl: amountBrl,
                        transactionId: details.transactionId,
                        paidAt: new Date().toISOString(),
                        rawDetails: details.rawDetails
                      });
                      setPaid(true);
                      setTimeout(() => {
                        onClose();
                        setPaid(false);
                      }, 2000);
                    } catch (err) {
                      console.error('Error completing Mercado Pago charge:', err);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onError={(err) => {
                    console.warn('Mercado Pago error in custom charge modal:', err);
                  }}
                />
              </div>
            )}
          </div>
          
          {selectedGateway !== 'PayPal' && selectedGateway !== 'MercadoPago' && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <button
                onClick={handleConfirmPayment}
                disabled={loading}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-lg shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5" />
                    <span>
                      {selectedGateway === 'PIX' 
                        ? (receiptFile ? 'Confirmar Pagamento com Comprovante' : 'Anexe o Comprovante PIX para Concluir')
                        : `Confirmar Pagamento (${selectedGateway})`}
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default CustomChargePaymentModal;
